/**
 * Feed health: may anything on screen be drawn as live right now (twin
 * slice 1, plan W3 step 5)?
 *
 * One answer per site, published by `useLiveData` (which owns the
 * stream) for the marks, the attention list (W5) and the lift columns
 * (W6). When it is CLAMPED, everything switches to last-known together
 * -- the canvas desaturates and the verdict reads "Reconnecting · last
 * update HH:MM" -- because a per-record age cannot see a dead stream:
 * no record arrives to be judged old until the boundary timers catch
 * up, and on a woken tab they have not run at all.
 *
 * Clamped when (the first that holds, in this order):
 *   no-stream     nothing holds a stream for this site;
 *   connecting    the stream has never delivered a data event;
 *   reconnecting  it had, and is now not open (closed, stale,
 *                 recovering, mid-connect) -- OR it has opened again
 *                 and nothing has arrived on the new response yet.
 *                 Response headers prove the webserver answered, not
 *                 that the platform behind it is publishing; the
 *                 clamp lifts on the first data event of THIS open
 *                 (`dataSinceOpen`), not on one left over from before;
 *   waking        the page was just shown again after being hidden or
 *                 restored, and no data event has arrived since;
 *   silent        the stream is open and has delivered data, but none
 *                 for `FEED_SILENT_AFTER_MS`. Pings keep the socket
 *                 and the read-idle watchdog happy, so this is the
 *                 one dead feed the transport never notices: data
 *                 silence with the heartbeat still running.
 *
 * `lastUpdateAtMs` is this device's `Date.now()` at the last data event
 * (the transport's `lastEventAt`), for "last update HH:MM". It is
 * published to the whole second, so a busy stream re-renders consumers
 * at most once a second rather than on every event.
 */
import { useSyncExternalStore } from 'react';
import type { FetchSseSiteSnapshot } from './fetch-sse';

export type FeedClampReason = 'no-stream' | 'connecting' | 'reconnecting' | 'waking' | 'silent';

/**
 * An open stream with no data event for this long is `silent`.
 *
 * Why 15 s: robot records are re-stamped at 1 Hz (the worker's roster),
 * lift states arrive at 1 Hz, and the transport already treats 15 s
 * without a byte as a wedge (`readIdleTimeoutMs`); 15 s of pings and
 * no data is the same wedge with the heartbeat still up. It is also the
 * plan's "everything switches to last-known together within 15 s".
 * Whether every site always has a 1 Hz publisher (a site with no robot
 * and no lift in its roster) is not established; at sg-nomad-01 both
 * flow.
 */
export const FEED_SILENT_AFTER_MS = 15_000;

export interface FeedHealth {
  readonly clamped: boolean;
  readonly reason: FeedClampReason | null;
  readonly lastUpdateAtMs: number | null;
}

/** What a site with no published health reads: clamped, no stream. */
export const NO_FEED: FeedHealth = Object.freeze({
  clamped: true,
  reason: 'no-stream',
  lastUpdateAtMs: null,
});

export interface FeedHealthInput {
  readonly snapshot: FetchSseSiteSnapshot | null;
  /** `Date.now()` when the page last woke; null when it has not since
   *  the stream was opened (or the wake has been satisfied). */
  readonly wokeAtMs: number | null;
  /** `Date.now()`, the clock `lastEventAt` is on; defaults to it. */
  readonly nowMs?: number;
}

function toSecond(ms: number | null): number | null {
  return ms === null ? null : Math.floor(ms / 1000) * 1000;
}

/** Pure: the health a stream snapshot and the wake state amount to. */
export function deriveFeedHealth({ snapshot, wokeAtMs, nowMs = Date.now() }: FeedHealthInput): FeedHealth {
  if (!snapshot) return NO_FEED;
  const last = snapshot.lastEventAt;
  const lastUpdateAtMs = toSecond(last);
  const clamp = (reason: FeedClampReason): FeedHealth => ({ clamped: true, reason, lastUpdateAtMs });
  if (last === null) return clamp('connecting');
  if (snapshot.state !== 'open' || !snapshot.dataSinceOpen) return clamp('reconnecting');
  if (wokeAtMs !== null && !(last > wokeAtMs)) return clamp('waking');
  // A negative or non-numeric age means the wall clock stepped; that
  // cannot vouch for the stream either. The next data event re-stamps.
  const age = nowMs - last;
  if (!(age >= 0 && age < FEED_SILENT_AFTER_MS)) return clamp('silent');
  return { clamped: false, reason: null, lastUpdateAtMs };
}

function sameHealth(a: FeedHealth, b: FeedHealth): boolean {
  return a.clamped === b.clamped && a.reason === b.reason && a.lastUpdateAtMs === b.lastUpdateAtMs;
}

const _bySite = new Map<string, FeedHealth>();
const _listeners = new Set<() => void>();

/** Publish `site`'s health; a value equal to the current one is a no-op,
 *  so subscribers see a new object only when something changed. */
export function publishFeedHealth(site: string, health: FeedHealth): void {
  const prev = _bySite.get(site);
  if (prev && sameHealth(prev, health)) return;
  _bySite.set(site, health);
  for (const l of _listeners) l();
}

export function getFeedHealth(site: string): FeedHealth {
  return _bySite.get(site) ?? NO_FEED;
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** `site`'s feed health, re-rendering only when it changes. */
export function useFeedHealth(site: string): FeedHealth {
  return useSyncExternalStore(
    subscribe,
    () => getFeedHealth(site),
    () => getFeedHealth(site),
  );
}

/** Tests only. */
export function _resetFeedHealthForTests(): void {
  _bySite.clear();
}
